import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DrawerClasses, SwipeableDrawer, useMediaQuery } from "@mui/material";
import { X } from "lucide-react";
import Cart from "./Cart";
import { useState } from "react";
import { Upsales } from "./Upsales";
import { useTrackingEvent } from "@/hooks/useTrackingEvent";
import { checkoutRoute, menuRoute } from "@/App";
import { useCartStore } from "@/store/CartStore";

interface CartModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

export function CartModal({ isOpen, closeModal }: CartModalProps) {
  const [showUpsales, setShowUpsales] = useState(false);
  const { locationId, menuId } = menuRoute.useParams();
  const { price, table } = useCartStore();
  const navigate = menuRoute.useNavigate();
  const isDesktop = useMediaQuery("only screen and (min-width : 768px)");
  const tracking = useTrackingEvent();

  if (isDesktop) {
    // Desktop: Centered modal
    return (
      <Dialog open={isOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-2xl w-full max-h-[90vh] h-[90vh] p-0 gap-0 rounded-xl overflow-hidden !z-[110]">
          {/* Close button - positioned in top-right */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white shadow-lg hover:bg-gray-50 border border-gray-200 transition-colors"
          >
            <X className="h-4 w-4 text-gray-600" />
          </button>

          {/* Cart content */}
          <div className="h-full overflow-hidden">
            <Cart setCartIsOpen={closeModal} setShowUpsales={setShowUpsales} />
          </div>

          {showUpsales && (
            <Upsales
              goToCheckout={() => {
                try {
                  tracking.capture("checkout_clicked", {
                    locationId,
                    value: price,
                  });
                } catch (error) {
                  console.error("Error capturing upsale clicked event:", error);
                }

                navigate({
                  to: checkoutRoute.to,
                  params: { menuId, locationId },
                  search: {
                    table: table,
                  },
                });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Mobile: Swipeable drawer (existing behavior)
  return (
    <SwipeableDrawer
      disableDiscovery
      disableSwipeToOpen
      anchor="bottom"
      open={isOpen}
      onClose={closeModal}
      onOpen={() => null}
      classes={
        {
          paper:
            "rounded-t-2xl !overflow-visible !bg-transparent !h-[75vh] !max-h-[75vh]",
        } as Partial<DrawerClasses>
      }
    >
      {/* Drawer handle */}
      <button
        type="button"
        onClick={closeModal}
        className="h-[32px] flex items-center justify-center mx-auto w-full absolute -top-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="bg-primary-foreground rounded-full h-[6px] block w-[12.5%]" />
      </button>

      {/* Cart content */}
      <Cart setCartIsOpen={closeModal} setShowUpsales={setShowUpsales} />

      {showUpsales && (
        <Upsales
          goToCheckout={() => {
            try {
              tracking.capture("checkout_clicked", {
                locationId,
                value: price,
              });
            } catch (error) {
              console.error("Error capturing upsale clicked event:", error);
            }

            navigate({
              to: checkoutRoute.to,
              params: { menuId, locationId },
              search: {
                table: table,
              },
            });
          }}
        />
      )}
    </SwipeableDrawer>
  );
}
